import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Download, Search, Calendar, Users, Shield, Sword,
  ArrowUpDown, ArrowUp, ArrowDown, Filter, BarChart3, Trophy, List
} from 'lucide-react';
import { adminService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import { getEventTypeTranslation, getEventTypeIcon } from '../../utils/eventTypeTranslation';

const AdminEventReport = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState([]);
  const [eventSummary, setEventSummary] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('members'); // 'members' | 'events'
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-01-01`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'totalConfirmado', direction: 'desc' });
  const [filterZero, setFilterZero] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [onlyApproved, setOnlyApproved] = useState(true);

  useEffect(() => {
    loadReport();
  }, [startDate, onlyApproved]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminService.getEventParticipationReport(startDate, onlyApproved);
      setReport(data.report || []);
      setEventSummary(data.eventSummary || []);
      setEventList(data.eventList || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3 h-3 text-cyan-400" />
      : <ArrowDown className="w-3 h-3 text-cyan-400" />;
  };

  const filteredMembers = useMemo(() => {
    let data = [...report];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(r =>
        r.membro?.toLowerCase().includes(term) ||
        r.personagens?.toLowerCase().includes(term)
      );
    }
    if (filterZero) data = data.filter(r => Number(r.totalConfirmado) > 0);
    data.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      aVal = Number(aVal) || 0; bVal = Number(bVal) || 0;
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return data;
  }, [report, searchTerm, sortConfig, filterZero]);

  const filteredEvents = useMemo(() => {
    let data = [...eventList];
    if (eventTypeFilter !== 'all') data = data.filter(e => e.type === eventTypeFilter);
    if (searchTerm && activeTab === 'events') {
      data = data.filter(e => e.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return data;
  }, [eventList, eventTypeFilter, searchTerm, activeTab]);

  const totals = useMemo(() => filteredMembers.reduce((acc, r) => ({
    pvpConfirmado: acc.pvpConfirmado + (Number(r.pvpConfirmado) || 0),
    pvpAusente: acc.pvpAusente + (Number(r.pvpAusente) || 0),
    bcConfirmado: acc.bcConfirmado + (Number(r.bcConfirmado) || 0),
    bcAusente: acc.bcAusente + (Number(r.bcAusente) || 0),
    outrosConfirmado: acc.outrosConfirmado + (Number(r.outrosConfirmado) || 0),
    outrosAusente: acc.outrosAusente + (Number(r.outrosAusente) || 0),
    totalConfirmado: acc.totalConfirmado + (Number(r.totalConfirmado) || 0),
    totalAusente: acc.totalAusente + (Number(r.totalAusente) || 0),
  }), { pvpConfirmado:0, pvpAusente:0, bcConfirmado:0, bcAusente:0, outrosConfirmado:0, outrosAusente:0, totalConfirmado:0, totalAusente:0 }),
  [filteredMembers]);

  const eventTotals = useMemo(() => {
    const map = {};
    eventSummary.forEach(s => { map[s.type] = Number(s.totalEvents); });
    return map;
  }, [eventSummary]);

  const maxTotal = useMemo(() =>
    Math.max(...report.map(r => Number(r.totalConfirmado) || 0), 1),
  [report]);

  const handleExportCSV = () => {
    const headers = ['Membro','Personagens','Telefone','Aprovação','PVP ✓','PVP ✗','BC ✓','BC ✗','Outros ✓','Outros ✗','Total ✓','Total ✗'];
    const rows = filteredMembers.map(r => [
      r.membro, r.personagens, r.telefone || '', r.dataAprovacao || '',
      r.pvpConfirmado, r.pvpAusente, r.bcConfirmado, r.bcAusente,
      r.outrosConfirmado, r.outrosAusente, r.totalConfirmado, r.totalAusente
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `participacao-eventos-${startDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Reusable cell with bar fill
  const Cell = ({ value, max, green = false }) => {
    const num = Number(value) || 0;
    const pct = Math.min((num / Math.max(Number(max), 1)) * 100, 100);
    return (
      <div className="relative min-w-[32px]">
        {num > 0 && (
          <div
            className={`absolute inset-0 rounded opacity-20 ${green ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${pct}%` }}
          />
        )}
        <span className={`relative z-10 font-mono text-sm ${num > 0 ? (green ? 'text-green-300' : 'text-red-300') : 'text-gray-600'}`}>
          {num}
        </span>
      </div>
    );
  };

  if (loading) return <LoadingPage message="Carregando relatório..." title="Relatório de Participação" />;

  const eventTypes = [...new Set(eventList.map(e => e.type))];

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 overflow-hidden">
      <div className="max-w-full mx-auto flex flex-col gap-4 h-full min-h-0 w-full">

        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/app/admin')}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-300 hover:text-white transition-all flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-cyan-400" />
                Relatório de Participação em Eventos
              </h1>
              <p className="text-gray-400 mt-1 text-sm">
                Participações confirmadas de todos os membros em eventos concluídos
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> A partir de
              </label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Search className="w-3 h-3" /> Buscar
              </label>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder={activeTab === 'members' ? 'Nome ou personagem...' : 'Nome do evento...'}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            {activeTab === 'members' && (
              <button onClick={() => setFilterZero(!filterZero)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${filterZero ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
                <Filter className="w-4 h-4" />
                {filterZero ? 'Com participação' : 'Todos os membros'}
              </button>
            )}
            <button onClick={() => setOnlyApproved(!onlyApproved)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${!onlyApproved ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
              <Users className="w-4 h-4" />
              {onlyApproved ? 'Apenas ativos' : 'Todos os status'}
            </button>
            {activeTab === 'events' && (
              <select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
                <option value="all">Todos os tipos</option>
                {eventTypes.map(t => (
                  <option key={t} value={t}>{getEventTypeIcon(t)} {getEventTypeTranslation(t)}</option>
                ))}
              </select>
            )}
            <button onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-green-400 text-sm font-medium transition-all">
              <Download className="w-4 h-4" /> Exportar CSV
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-gray-400">Membros ativos</span>
            </div>
            <p className="text-2xl font-bold text-white">{report.length}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-red-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Sword className="w-4 h-4 text-red-400" />
              <span className="text-xs text-gray-400">Eventos PVP</span>
            </div>
            <p className="text-2xl font-bold text-red-400">{eventTotals['PVP'] || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-amber-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-gray-400">Eventos BC</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{eventTotals['BC'] || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-400">Outros eventos</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">
              {Object.entries(eventTotals).filter(([k]) => !['PVP','BC'].includes(k)).reduce((s,[,v]) => s+v, 0)}
            </p>
          </div>
        </div>

        {error && (
          <div className="flex-shrink-0 bg-red-900/20 rounded-xl p-4 border border-red-500/20 text-red-400 text-center">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-3">
          <button onClick={() => setActiveTab('members')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'members' ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/20' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
            <Users className="w-4 h-4" /> Por Membro ({filteredMembers.length})
          </button>
          <button onClick={() => setActiveTab('events')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'events' ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/20' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
            <List className="w-4 h-4" /> Por Evento ({filteredEvents.length})
          </button>
        </div>

        {/* Tab: Members */}
        {activeTab === 'members' && (
          <div className="flex-1 min-h-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-sm" style={{ minWidth: '900px' }}>
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-white/10 bg-slate-800/95">
                    {/* Member */}
                    <th className="text-left px-4 py-3 text-gray-400 font-medium cursor-pointer hover:text-white sticky left-0 bg-slate-800/95 z-30"
                      onClick={() => handleSort('membro')}>
                      <div className="flex items-center gap-1">Membro {getSortIcon('membro')}</div>
                    </th>
                    <th className="text-left px-3 py-3 text-gray-400 font-medium">Personagens</th>
                    <th className="text-left px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Aprovação</th>
                    {/* PVP */}
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('pvpConfirmado')}>
                      <div className="flex items-center justify-center gap-1 text-red-400">
                        <Sword className="w-3 h-3" /> PVP ✓ {getSortIcon('pvpConfirmado')}
                      </div>
                    </th>
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('pvpAusente')}>
                      <div className="flex items-center justify-center gap-1 text-red-400/50">PVP ✗ {getSortIcon('pvpAusente')}</div>
                    </th>
                    {/* BC */}
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('bcConfirmado')}>
                      <div className="flex items-center justify-center gap-1 text-amber-400">
                        <Shield className="w-3 h-3" /> BC ✓ {getSortIcon('bcConfirmado')}
                      </div>
                    </th>
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('bcAusente')}>
                      <div className="flex items-center justify-center gap-1 text-amber-400/50">BC ✗ {getSortIcon('bcAusente')}</div>
                    </th>
                    {/* Outros */}
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('outrosConfirmado')}>
                      <div className="flex items-center justify-center gap-1 text-purple-400">Outros ✓ {getSortIcon('outrosConfirmado')}</div>
                    </th>
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('outrosAusente')}>
                      <div className="flex items-center justify-center gap-1 text-purple-400/50">Outros ✗ {getSortIcon('outrosAusente')}</div>
                    </th>
                    {/* Total */}
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('totalConfirmado')}>
                      <div className="flex items-center justify-center gap-1 text-green-400">Total ✓ {getSortIcon('totalConfirmado')}</div>
                    </th>
                    <th className="px-3 py-3 cursor-pointer hover:text-white text-center whitespace-nowrap" onClick={() => handleSort('totalAusente')}>
                      <div className="flex items-center justify-center gap-1 text-gray-400">Total ✗ {getSortIcon('totalAusente')}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-16 text-gray-400">
                        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Nenhum dado encontrado</p>
                      </td>
                    </tr>
                  ) : filteredMembers.map((row, idx) => (
                    <tr key={row.userId || idx} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${Number(row.totalConfirmado) === 0 ? 'opacity-40' : ''}`}>
                      <td className="px-4 py-3 sticky left-0 bg-slate-800/95 z-10">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {row.membro?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <span className="text-white font-medium whitespace-nowrap">{row.membro}</span>
                            {!onlyApproved && row.statusMembro !== 'APPROVED' && (
                              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${
                                row.statusMembro === 'SUSPENDED' ? 'bg-gray-500/20 text-gray-400' :
                                row.statusMembro === 'REJECTED'  ? 'bg-red-500/20 text-red-400' :
                                row.statusMembro === 'PENDING'   ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-white/10 text-gray-300'
                              }`}>
                                {row.statusMembro}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 max-w-[180px]">
                        <div className="flex flex-wrap gap-1">
                          {(row.personagens || '').split(',').map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-xs text-gray-300 whitespace-nowrap">
                              {n.trim()}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">{row.dataAprovacao || '—'}</td>
                      <td className="px-3 py-3 text-center"><Cell value={row.pvpConfirmado} max={eventTotals['PVP']} green /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.pvpAusente} max={eventTotals['PVP']} /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.bcConfirmado} max={eventTotals['BC']} green /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.bcAusente} max={eventTotals['BC']} /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.outrosConfirmado} max={10} green /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.outrosAusente} max={10} /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.totalConfirmado} max={maxTotal} green /></td>
                      <td className="px-3 py-3 text-center"><Cell value={row.totalAusente} max={maxTotal} /></td>
                    </tr>
                  ))}
                </tbody>
                {filteredMembers.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-white/20 bg-white/5 font-bold">
                      <td className="px-4 py-3 text-white sticky left-0 bg-slate-700/95 z-10">
                        TOTAL ({filteredMembers.length})
                      </td>
                      <td colSpan="2" className="px-3 py-3" />
                      <td className="px-3 py-3 text-center text-green-400 font-mono">{totals.pvpConfirmado}</td>
                      <td className="px-3 py-3 text-center text-red-400 font-mono">{totals.pvpAusente}</td>
                      <td className="px-3 py-3 text-center text-green-400 font-mono">{totals.bcConfirmado}</td>
                      <td className="px-3 py-3 text-center text-red-400 font-mono">{totals.bcAusente}</td>
                      <td className="px-3 py-3 text-center text-green-400 font-mono">{totals.outrosConfirmado}</td>
                      <td className="px-3 py-3 text-center text-red-400 font-mono">{totals.outrosAusente}</td>
                      <td className="px-3 py-3 text-center text-green-400 font-mono">{totals.totalConfirmado}</td>
                      <td className="px-3 py-3 text-center text-red-400 font-mono">{totals.totalAusente}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 flex flex-wrap gap-4 text-xs text-gray-500">
              <span className="text-green-400/70">✓ Confirmado (qualquer status ativo)</span>
              <span className="text-red-400/70">✗ Ausente / Recusado</span>
              <span className="flex items-center gap-1"><Sword className="w-3 h-3 text-red-400" /> PVP</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-amber-400" /> BC</span>
              <span className="text-purple-400/70">Outros = Reunião, etc.</span>
            </div>
          </div>
        )}

        {/* Tab: Events */}
        {activeTab === 'events' && (
          <div className="flex-1 min-h-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-sm" style={{ minWidth: '640px' }}>
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-white/10 bg-slate-800/95">
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Evento</th>
                    <th className="text-left px-3 py-3 text-gray-400 font-medium">Tipo</th>
                    <th className="text-left px-3 py-3 text-gray-400 font-medium">Data</th>
                    <th className="text-center px-3 py-3 text-green-400 font-medium">Confirmados ✓</th>
                    <th className="text-center px-3 py-3 text-red-400 font-medium">Negados ✗</th>
                    <th className="text-center px-3 py-3 text-yellow-400 font-medium">Ausentes ⚠</th>
                    <th className="text-center px-3 py-3 text-gray-400 font-medium">Total</th>
                    <th className="text-center px-3 py-3 text-cyan-400 font-medium">Taxa</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-16 text-gray-400">
                        <List className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Nenhum evento encontrado</p>
                      </td>
                    </tr>
                  ) : filteredEvents.map((evt, idx) => {
                    const conf = Number(evt.confirmados) || 0;
                    const neg = Number(evt.negados) || 0;
                    const aus = Number(evt.ausentes) || 0;
                    const total = Number(evt.total) || 0;
                    const taxa = total > 0 ? Math.round((conf / total) * 100) : 0;
                    const typeColors = { PVP: 'text-red-400 bg-red-500/10 border-red-500/20', BC: 'text-amber-400 bg-amber-500/10 border-amber-500/20' };
                    const typeColor = typeColors[evt.type] || 'text-purple-400 bg-purple-500/10 border-purple-500/20';
                    return (
                      <tr key={evt.id || idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 text-white font-medium">{evt.name}</td>
                        <td className="px-3 py-3">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${typeColor}`}>
                            {getEventTypeIcon(evt.type)} {getEventTypeTranslation(evt.type)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-gray-400 whitespace-nowrap">{evt.data}</td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-green-400 font-mono font-bold">{conf}</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`font-mono ${neg > 0 ? 'text-red-400' : 'text-gray-600'}`}>{neg}</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`font-mono ${aus > 0 ? 'text-yellow-400' : 'text-gray-600'}`}>{aus}</span>
                        </td>
                        <td className="px-3 py-3 text-center text-gray-400 font-mono">{total}</td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div className={`h-full ${taxa >= 70 ? 'bg-green-500' : taxa >= 40 ? 'bg-yellow-400' : 'bg-red-400'} rounded-full`} style={{ width: `${taxa}%` }} />
                            </div>
                            <span className={`text-xs font-mono ${taxa >= 70 ? 'text-green-400' : taxa >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {taxa}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEventReport;

